import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/axios';
import { useAuth } from '../../contexts/KeycloakContext';
import CreditInsightReport from '../credits/CreditInsightReport';
import Pagination from '../../ui/Pagination';
import Banner from '../../ui/Banner';
import './Members.css';
import '../credits/CreditInsightReport.css';

const PersonalCreditUsage = () => {
  const { keycloak } = useAuth();
  const [activeTab, setActiveTab] = useState('insights');

  // Date Helper for Insights
  const getPresetDates = (preset) => {
    const now = new Date();
    let start = new Date();
    let end = new Date();
    const formatLocalPart = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    switch (preset) {
      case 'Today':
        start = new Date(now);
        end = new Date(now);
        break;
      case 'This Week': {
        const day = now.getDay();
        const diffToMonday = day === 0 ? 6 : day - 1;
        start = new Date(now);
        start.setDate(now.getDate() - diffToMonday);
        end = new Date(start);
        end.setDate(start.getDate() + 4);
        break;
      }
      case 'This Month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'This Year':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
      default:
        return null;
    }

    return {
      startDate: formatLocalPart(start),
      endDate: formatLocalPart(end),
    };
  };

  const [insightDate, setInsightDate] = useState(getPresetDates('Today'));
  const [activePreset, setActivePreset] = useState('Today');
  const [insightGroup, setInsightGroup] = useState('day');

  const applyInsightPreset = (p) => {
    const range = getPresetDates(p);
    if (range) {
      setInsightDate(range);
      setActivePreset(p);
      if (p === 'This Year') setInsightGroup('month');
      else setInsightGroup('day');
    }
  };

  const getTransactionLabel = (type) => {
    if (type === 'USAGE' || type === 'ENRICHMENT') return 'ENRICHMENT';
    if (type === 'B2C_RECOMMENDATION') return 'SEARCH PERSONA';
    if (type === 'MARKET_ANALYSIS') return 'MARKET ANALYSIS';
    if (type === 'SEARCH') return 'SEARCH';
    return String(type || '').replace(/_/g, ' ');
  };

  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 0, total: 0, limit: 10 });
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    transactionType: '',
  });
  const [sort, setSort] = useState({
    sortBy: 'created_at',
    sortOrder: 'DESC',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${keycloak.token}` } };
      const { startDate, endDate, transactionType } = filters;
      const { sortBy, sortOrder } = sort;

      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(transactionType && { transactionType }),
      }).toString();

      const res = await axios.get(`/credits/my/history?${queryParams}`, config);
      setUsage(res.data.data);
      setPagination((prev) => ({ ...prev, total: res.data.total }));
    } catch (err) {
      console.error('[PersonalCreditUsage] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [keycloak.token, pagination.page, pagination.limit, filters, sort]);

  useEffect(() => {
    if (activeTab === 'usage') {
      fetchData();
    }
  }, [activeTab, filters, sort, pagination.page, pagination.limit, fetchData]);

  return (
    <div className="tm-page">
      <Banner
        title="Credit Usage"
        subtitle="Track your personal credit consumption and usage insights."
        type="page-header"
      />

      <div className="tm-section-tabs">
        <button
          className={`tm-section-tab ${activeTab === 'insights' ? 'active' : ''}`}
          onClick={() => setActiveTab('insights')}
        >
          <i className="fas fa-chart-line"></i> Usage Insights
        </button>
        <div className="tm-tab-divider"></div>
        <button
          className={`tm-section-tab ${activeTab === 'usage' ? 'active' : ''}`}
          onClick={() => setActiveTab('usage')}
        >
          <i className="fas fa-list-ul"></i> Usage Activity
        </button>
      </div>
      <div className="tm-horizontal-divider"></div>

      {activeTab === 'insights' && (
        <>
          <div
            className="tm-filter-bar animate-fade-in"
            style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <div
              className="tm-tabs"
              style={{
                display: 'flex',
                gap: '4px',
                background: '#f1f5f9',
                padding: '4px',
                borderRadius: '8px',
              }}
            >
              {['Today', 'This Week', 'This Month', 'This Year'].map((p) => (
                <button
                  key={p}
                  className={`tm-tab-pill ${activePreset === p ? 'active' : ''}`}
                  onClick={() => applyInsightPreset(p)}
                >
                  {p}
                </button>
              ))}
            </div>

            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{ marginRight: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>VIEW BY</span>
                <select
                  className="tm-select sm"
                  value={insightGroup || 'day'}
                  onChange={(e) => setInsightGroup(e.target.value)}
                  style={{
                    height: '36px',
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: '0 12px',
                    borderRadius: '8px',
                  }}
                >
                  <option value="day">Day</option>
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                </select>
              </div>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#374151',
                }}
              >
                <span>From</span>
                <div className="tm-search" style={{ height: '36px', minWidth: '150px' }}>
                  <input
                    type="date"
                    value={insightDate.startDate}
                    onChange={(e) => {
                      setInsightDate((prev) => ({ ...prev, startDate: e.target.value }));
                      setActivePreset('Custom');
                    }}
                  />
                </div>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#374151',
                }}
              >
                <span>To</span>
                <div className="tm-search" style={{ height: '36px', minWidth: '150px' }}>
                  <input
                    type="date"
                    value={insightDate.endDate}
                    onChange={(e) => {
                      setInsightDate((prev) => ({ ...prev, endDate: e.target.value }));
                      setActivePreset('Custom');
                    }}
                  />
                </div>
              </label>
            </div>
          </div>
          <div className="animate-fade-in">
            <CreditInsightReport isPersonal={true} dateRange={insightDate} groupBy={insightGroup} />
          </div>
        </>
      )}

      {activeTab === 'usage' && (
        <div className="animate-fade-in">
          <div className="tm-filter-bar">
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                minWidth: '180px',
                fontSize: '12px',
                fontWeight: 500,
                color: '#374151',
              }}
            >
              <span>From</span>
              <div className="tm-search" style={{ minWidth: '150px', flex: 1 }}>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
                  title="Start Date"
                />
              </div>
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                minWidth: '180px',
                fontSize: '12px',
                fontWeight: 500,
                color: '#374151',
              }}
            >
              <span>To</span>
              <div className="tm-search" style={{ minWidth: '150px', flex: 1 }}>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
                  title="End Date"
                />
              </div>
            </label>
            <select
              className="tm-select"
              value={filters.transactionType}
              onChange={(e) => setFilters((f) => ({ ...f, transactionType: e.target.value }))}
            >
              <option value="">All Types</option>
              <option value="SEARCH">Search</option>
              <option value="ENRICHMENT">Enrichment</option>
              <option value="B2C_RECOMMENDATION">Search Persona</option>
              <option value="MARKET_ANALYSIS">Market Analysis</option>
            </select>
            <button
              className="tm-btn tm-btn-outline"
              onClick={() => setFilters({ startDate: '', endDate: '', transactionType: '' })}
            >
              <i className="fas fa-undo"></i> Reset
            </button>
          </div>

          <Pagination
            columns={[
              { key: 'created_at', label: 'Timestamp', sortable: true, width: '150px' },
              { key: 'transaction_type', label: 'Type', width: '140px' },
              { key: 'amount', label: 'Amount', sortable: true, width: '100px' },
              { key: 'previous_balance', label: 'Before', width: '100px' },
              { key: 'new_balance', label: 'After', width: '100px' },
            ]}
            data={usage}
            onSort={(key) =>
              setSort((s) => ({
                sortBy: key,
                sortOrder: s.sortBy === key && s.sortOrder === 'DESC' ? 'ASC' : 'DESC',
              }))
            }
            renderRow={(u) => (
              <tr key={u.id}>
                <td style={{ fontSize: '11px', color: '#64748b' }}>
                  {new Date(u.created_at).toLocaleString()}
                </td>
                <td>
                  <span
                    className="tm-role-pill tm-role-organization_user"
                    style={{ fontSize: '10px', textTransform: 'uppercase' }}
                  >
                    {getTransactionLabel(u.transaction_type)}
                  </span>
                </td>
                <td style={{ color: u.amount < 0 ? '#ef4444' : '#10b981', fontWeight: '700' }}>
                  {u.amount > 0 ? '+' : ''}
                  {u.amount.toLocaleString()}
                </td>
                <td style={{ fontSize: '12px', color: '#94a3b8' }}>
                  {(u.previous_balance || 0).toLocaleString()}
                </td>
                <td style={{ fontSize: '12px', color: '#94a3b8' }}>
                  {(u.new_balance || 0).toLocaleString()}
                </td>
              </tr>
            )}
            loading={loading}
            currentPage={pagination.page + 1}
            totalPages={Math.max(1, Math.ceil(pagination.total / pagination.limit))}
            onPageChange={(p) => setPagination((prev) => ({ ...prev, page: p - 1 }))}
            itemsPerPage={pagination.limit}
            onItemsPerPageChange={(limit) => setPagination((prev) => ({ ...prev, page: 0, limit }))}
            totalItems={pagination.total}
            itemsName="records"
            className="tm-usage-table-card"
            sortBy={sort.sortBy}
            sortOrder={sort.sortOrder}
          />
        </div>
      )}
    </div>
  );
};

export default PersonalCreditUsage;
